import { type ComponentType, lazy, Suspense } from 'react'
import { Redirect, Route, Switch, useParams } from 'wouter'
import { Shell } from './components/Shell'
import { Spinner } from './components/ui'
import { ApiError } from './lib/api'
import { useMe } from './lib/hooks'
import { InviteAccept, Login, Signup } from './pages/Auth'
import { Home } from './pages/Home'
import { Workspaces } from './pages/Workspaces'

// Heavier pages load on first visit; the shell, auth and home ship in the initial bundle.
// A tab opened before a deploy asks for chunk names that no longer exist: reload once to pick up the new build.
const RELOADED = 'plan:chunk-reload'
const lazyPage = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() =>
    load().then(
      m => {
        try {
          sessionStorage.removeItem(RELOADED)
        } catch {}
        return { default: m[name] }
      },
      (e: unknown) => {
        let again = false
        try {
          again = Date.now() - Number(sessionStorage.getItem(RELOADED) ?? 0) > 10_000
          if (again) sessionStorage.setItem(RELOADED, String(Date.now()))
        } catch {}
        if (again) {
          window.location.reload()
          return new Promise<never>(() => {})
        }
        throw e
      },
    ),
  )
const ChangesetDetail = lazyPage(() => import('./pages/ChangesetDetail'), 'ChangesetDetail')
const Cycle = lazyPage(() => import('./pages/Cycle'), 'Cycle')
const Data = lazyPage(() => import('./pages/Data'), 'Data')
const ConnectionDetail = lazyPage(() => import('./pages/Connections'), 'ConnectionDetail')
const ConnectionWizard = lazyPage(() => import('./pages/ConnectionWizard'), 'ConnectionWizard')
const AddDatasets = lazyPage(() => import('./pages/ConnectionWizard'), 'AddDatasets')
const DatasetDetail = lazyPage(() => import('./pages/DatasetDetail'), 'DatasetDetail')
const PagesIndex = lazyPage(() => import('./pages/Pages'), 'PagesIndex')
const PageView = lazyPage(() => import('./pages/PageView'), 'PageView')
const ImportWizard = lazyPage(() => import('./pages/ImportWizard'), 'ImportWizard')
const Model = lazyPage(() => import('./pages/Model'), 'Model')
const Plans = lazyPage(() => import('./pages/Plans'), 'Plans')
const MyPlan = lazyPage(() => import('./pages/MyPlan'), 'MyPlanPage')
const Reviews = lazyPage(() => import('./pages/Reviews'), 'Reviews')
const Settings = lazyPage(() => import('./pages/Settings'), 'Settings')
const Views = lazyPage(() => import('./pages/Views'), 'ViewsPage')

function Authed({ children }: { children: React.ReactNode }) {
  const me = useMe()
  if (me.isLoading)
    return (
      <div className="empty" style={{ marginTop: '30vh' }}>
        <Spinner />
      </div>
    )
  if (me.error instanceof ApiError && me.error.status === 401)
    return <Redirect to={`/login?next=${encodeURIComponent(location.pathname)}`} />
  if (me.error)
    return (
      <div className="page">
        <div className="error-box">Could not reach the planning service: {me.error.message}</div>
      </div>
    )
  return <>{children}</>
}

function Launch() {
  const me = useMe()
  const last = localStorage.getItem('plan.lastWorkspace')
  const ws = me.data?.workspaces ?? []
  const target = ws.find(w => w.id === last && w.status === 'ready') ?? ws.find(w => w.status === 'ready')
  return <Redirect to={target ? `/w/${target.id}` : '/workspaces'} />
}

function WorkspaceRoutes() {
  const { wid } = useParams<{ wid: string }>()
  return (
    <Shell wid={wid}>
      <Suspense
        fallback={
          <div className="page">
            <Spinner />
          </div>
        }
      >
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/plan" component={MyPlan} />
          <Route path="/reviews" component={Reviews} />
          <Route path="/changesets/:id" component={ChangesetDetail} />
          <Route path="/plans" component={Plans} />
          <Route path="/views" component={Views} />
          <Route path="/views/:vid" component={Views} />
          <Route path="/cycle" component={Cycle} />
          <Route path="/pages" component={PagesIndex} />
          <Route path="/pages/:id" component={PageView} />
          <Route path="/model" component={Model} />
          <Route path="/data" component={Data} />
          <Route path="/data/connections/new" component={ConnectionWizard} />
          <Route path="/data/connections/:id/add" component={AddDatasets} />
          <Route path="/data/connections/:id" component={ConnectionDetail} />
          <Route path="/data/datasets/:id" component={DatasetDetail} />
          <Route path="/settings" component={Settings} />
          <Route>
            <div className="page">
              <div className="empty">Not found.</div>
            </div>
          </Route>
        </Switch>
      </Suspense>
    </Shell>
  )
}

export function App() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/invite/:token" component={InviteAccept} />
      <Route path="/workspaces">
        <Authed>
          <Workspaces />
        </Authed>
      </Route>
      <Route path="/new">
        <Authed>
          <Suspense
            fallback={
              <div className="page">
                <Spinner />
              </div>
            }
          >
            <ImportWizard />
          </Suspense>
        </Authed>
      </Route>
      <Route path="/w/:wid" nest>
        <Authed>
          <WorkspaceRoutes />
        </Authed>
      </Route>
      <Route path="/">
        <Authed>
          <Launch />
        </Authed>
      </Route>
    </Switch>
  )
}
